import { Box, Divider, Flex } from '@televet/kibble-ui';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { useCallback, useMemo } from 'react';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { GetAppointmentAiRecapInfoQuery } from 'shared/types/graphql';
import { formatToSimpleDateWithTime } from 'shared/utils/formatDate';
import { parseTranscript, ParseTranscriptStatus } from '../../utils';

type TranscriptTabProps = {
  recordings: NonNullable<GetAppointmentAiRecapInfoQuery['findManyRecap']>;
};

const TranscriptTab = ({ recordings }: TranscriptTabProps): JSX.Element => {
  const { isMobile } = useResolutionProvider();
  const toast = useToast();

  const transcriptsFormatted = useMemo(
    () =>
      (recordings ?? [])
        .slice()
        .reverse()
        .map((recap) => parseTranscript(recap.id, recap.transcriptionText || '')),
    [recordings],
  );

  const onCopyTranscript = useCallback((): void => {
    const combined = transcriptsFormatted
      .map((t, i) => {
        const content =
          t.status === ParseTranscriptStatus.FormattedCompletely
            ? t.conversation.map((entry) => `${entry.time} ${entry.speaker}: ${entry.content}`).join('\n')
            : t.fullConversation;
        return `Transcript #${i + 1}\n${content}`;
      })
      .join('\n\n');
    navigator.clipboard.writeText(combined).then(
      () => toast({ title: 'Transcript copied!', status: 'success' }),
      () => toast({ title: 'Failed to copy transcript', status: 'error' }),
    );
  }, [transcriptsFormatted, toast]);

  return (
    <Flex direction="column" height="100%" pb={5} overflow="auto">
      <Flex height="100%" direction="column" pb={5}>
        {!isMobile && (
          <Flex justifyContent="space-between" direction={['column', 'row']} mb={5}>
            <Box>
              <Text fontWeight="bold" size="md" as="p" mb={1}>
                Transcript
              </Text>
              <Text as="p" fontSize="sm" color="text.subtle">
                {recordings.length > 0 &&
                  `Generated ${formatToSimpleDateWithTime(recordings[recordings.length - 1].createdAt)}`}
              </Text>
            </Box>
            <Button
              size="md"
              variant="secondarySubtle"
              iconName="twoSquares"
              onClick={onCopyTranscript}
              disabled={recordings.length === 0}
              mt={[4, 0]}
            >
              Copy Transcript
            </Button>
          </Flex>
        )}
        <Flex direction="column" height="100%" flex={1} overflow="auto">
          <Box background="background.subtle" borderRadius="md" p={isMobile ? 2 : 5}>
            {transcriptsFormatted.map((transcript, index) => (
              <Box key={transcript.id} mb={index === transcriptsFormatted.length - 1 ? 0 : 5}>
                <Text size="md" fontWeight="bold" mb={3} as="p">
                  Recording #{index + 1}
                </Text>
                <Divider borderColor="text.success" mb={isMobile ? 2 : 4} />
                {transcript.status === ParseTranscriptStatus.FormattedCompletely ? (
                  transcript.conversation.map((entry, entryIndex) => (
                    <Box key={entry.key} mb={entryIndex === transcript.conversation.length - 1 ? 0 : 2}>
                      <Text size="sm" color="text.subtle" as="p" mb={2}>
                        {entry.time}
                      </Text>
                      <Text size="md" as="p">
                        {entry.speaker}: {entry.content}
                      </Text>
                    </Box>
                  ))
                ) : (
                  <Text size="md" as="p" whiteSpace="pre-wrap">
                    {transcript.fullConversation}
                  </Text>
                )}
              </Box>
            ))}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default TranscriptTab;
